import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import MainLayout from '../../../../../components/layout/MainLayout';
import { getBackofficeSidebarItems } from '../../../shell/backofficeSidebar';
import { useAuth } from '../../../../../context/AuthContext';
import { resolveApiErrorMessage } from '../../../../../utils/apiErrorMessage';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3001';

type RoleRow = { id: string; name: string; description?: string | null; isSystem: boolean };

type DepartmentRow = { id: string; name: string; slug: string; isActive: boolean };

type EmployeeDetails = {
  id: string;
  employeeCode: string;
  fullName: string;
  workEmail?: string | null;
  phone?: string | null;
  salaryCurrency: string;
  baseSalary: number;
  employmentKind: 'SIMPLE' | 'SYSTEM_USER';
  status: 'ACTIVE' | 'BLOCKED';
  personnelRecordStatus?: 'ACTIVE' | 'BLOCKED';
  notes?: string | null;
  createdAt?: string;
  blockedReason?: string | null;
  blockedAt?: string | null;
  isPrimaryAdmin?: boolean;
  departments: Array<{ id: string; name: string; slug: string; baseSalary: number }>;
  user?: {
    id: string;
    email: string;
    type: string;
    status: string;
    isEmailVerified?: boolean;
    blockedReason?: string | null;
    blockedAt?: string | null;
    roles?: Array<{ id: string; name: string }>;
    departmentRoles?: Array<{
      departmentId: string;
      departmentName: string;
      departmentSlug: string;
      roleId: string;
      roleName: string;
    }>;
  } | null;
};

function formatDate(iso?: string | null) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export default function EmployeeDetailsPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { authorizedFetch, auth } = useAuth();
  const { employeeId = '' } = useParams();
  const [details, setDetails] = useState<EmployeeDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [salaryCurrency, setSalaryCurrency] = useState('USD');
  const [departmentSalaries, setDepartmentSalaries] = useState<Record<string, string>>({});

  const [rolesCatalog, setRolesCatalog] = useState<RoleRow[]>([]);
  const [allDepartments, setAllDepartments] = useState<DepartmentRow[]>([]);
  const [globalRoleIds, setGlobalRoleIds] = useState<string[]>([]);
  const [deptRoleByDepartmentId, setDeptRoleByDepartmentId] = useState<Record<string, string>>({});
  const [rolesSectionLoading, setRolesSectionLoading] = useState(false);
  const [globalRolesSaving, setGlobalRolesSaving] = useState(false);
  const [deptRolesSaving, setDeptRolesSaving] = useState(false);
  const [statusSaving, setStatusSaving] = useState(false);
  const [convertSaving, setConvertSaving] = useState(false);
  const [downgradeSaving, setDowngradeSaving] = useState(false);
  const [convertEmail, setConvertEmail] = useState('');
  const [convertRoleId, setConvertRoleId] = useState('');

  const rbacUserId = details?.user?.id ?? null;
  const canManageRoles = Boolean(rbacUserId);
  const isSelf = Boolean(auth?.email && details?.user?.email && auth.email === details.user.email);

  const loadDetails = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await authorizedFetch(`${API_BASE}/auth/workspace/employees/${employeeId}/details`);
      if (!res.ok) throw new Error(await resolveApiErrorMessage(res, 'Unable to load employee details.'));
      const data = (await res.json()) as EmployeeDetails;
      setDetails(data);
      setSalaryCurrency(data.salaryCurrency || 'USD');
      setDepartmentSalaries(
        Object.fromEntries((data.departments || []).map((row) => [row.id, String(row.baseSalary || 0)])),
      );
      const fromUserRoles = (data.user?.roles || []).map((r) => r.id);
      setGlobalRoleIds(fromUserRoles);
      const drMap: Record<string, string> = {};
      (data.user?.departmentRoles || []).forEach((row) => {
        if (row.departmentId && row.roleId && !drMap[row.departmentId]) {
          drMap[row.departmentId] = row.roleId;
        }
      });
      setDeptRoleByDepartmentId(drMap);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load employee details.');
    } finally {
      setLoading(false);
    }
  }, [authorizedFetch, employeeId]);

  useEffect(() => {
    if (!employeeId) return;
    void loadDetails();
  }, [employeeId, loadDetails]);

  useEffect(() => {
    if (!actionMessage) return;
    const timer = window.setTimeout(() => setActionMessage(null), 6500);
    return () => window.clearTimeout(timer);
  }, [actionMessage]);

  useEffect(() => {
    (async () => {
      setRolesSectionLoading(true);
      try {
        const [rolesRes, depRes] = await Promise.all([
          authorizedFetch(`${API_BASE}/rbac/roles`),
          authorizedFetch(`${API_BASE}/departments/workspace`),
        ]);
        if (rolesRes.ok) {
          const rows = (await rolesRes.json()) as RoleRow[];
          setRolesCatalog((rows || []).filter((r) => !r.isSystem));
        }
        if (depRes.ok) {
          const rows = (await depRes.json()) as DepartmentRow[];
          setAllDepartments((Array.isArray(rows) ? rows : []).filter((d) => d.isActive));
        }
      } finally {
        setRolesSectionLoading(false);
      }
    })();
  }, [authorizedFetch]);

  const computedTotal = useMemo(
    () => Object.values(departmentSalaries).reduce((sum, value) => sum + (Number(value) || 0), 0),
    [departmentSalaries],
  );

  const saveCompensation = async () => {
    if (!details) return;
    setSaving(true);
    setError(null);
    setActionMessage(null);
    try {
      const res = await authorizedFetch(`${API_BASE}/auth/workspace/employees/${details.id}/compensation`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          salaryCurrency,
          departmentCompensations: details.departments.map((dep) => ({
            departmentId: dep.id,
            baseSalary: Number(departmentSalaries[dep.id] || 0),
          })),
        }),
      });
      if (!res.ok) throw new Error(await resolveApiErrorMessage(res, 'Unable to save compensation.'));
      const updated = (await res.json()) as EmployeeDetails;
      setDetails(updated);
      setActionMessage('Compensation saved.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to save compensation.');
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async () => {
    if (!details) return;
    setStatusSaving(true);
    setError(null);
    setActionMessage(null);
    const target = details.status === 'ACTIVE' ? 'BLOCKED' : 'ACTIVE';
    try {
      const res = await authorizedFetch(`${API_BASE}/auth/workspace/employees/${employeeId}/status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: target }),
      });
      if (!res.ok) {
        throw new Error(
          await resolveApiErrorMessage(res, t('backoffice.employees_status_error', 'Unable to update status.')),
        );
      }
      setActionMessage(target === 'BLOCKED' ? 'Employee blocked.' : 'Employee re-enabled.');
      await loadDetails();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to update status.');
    } finally {
      setStatusSaving(false);
    }
  };

  const saveGlobalRoles = async () => {
    if (!rbacUserId) return;
    setGlobalRolesSaving(true);
    setError(null);
    setActionMessage(null);
    try {
      const res = await authorizedFetch(`${API_BASE}/rbac/users/${rbacUserId}/roles`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ roleIds: globalRoleIds }),
      });
      if (!res.ok) throw new Error(await resolveApiErrorMessage(res, 'Unable to save workspace roles.'));
      setActionMessage('Workspace roles updated.');
      await loadDetails();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to save workspace roles.');
    } finally {
      setGlobalRolesSaving(false);
    }
  };

  const saveDepartmentRoles = async () => {
    if (!rbacUserId) return;
    setDeptRolesSaving(true);
    setError(null);
    setActionMessage(null);
    try {
      const assignments = Object.entries(deptRoleByDepartmentId)
        .filter(([, roleId]) => Boolean(roleId))
        .map(([departmentId, roleId]) => ({ departmentId, roleIds: [roleId] }));
      const res = await authorizedFetch(`${API_BASE}/rbac/users/${rbacUserId}/department-roles`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ assignments }),
      });
      if (!res.ok) throw new Error(await resolveApiErrorMessage(res, 'Unable to save department roles.'));
      setActionMessage('Department roles updated.');
      await loadDetails();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to save department roles.');
    } finally {
      setDeptRolesSaving(false);
    }
  };

  const convertToUser = async () => {
    if (!details || details.employmentKind !== 'SIMPLE') return;
    const email = convertEmail.trim().toLowerCase();
    if (!email || !convertRoleId) {
      setError('Enter a work email and select a default role for conversion.');
      return;
    }
    const departmentIds = details.departments.map((d) => d.id);
    if (!departmentIds.length) {
      setError('Assign at least one department before converting to a system user.');
      return;
    }
    setConvertSaving(true);
    setError(null);
    setActionMessage(null);
    try {
      const res = await authorizedFetch(`${API_BASE}/auth/workspace/employees/${details.id}/convert-to-user`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          roleIds: [convertRoleId],
          departmentIds,
        }),
      });
      if (!res.ok) throw new Error(await resolveApiErrorMessage(res, 'Unable to convert employee.'));
      const body = (await res.json()) as { id: string };
      setActionMessage('Invite sent. The employee can complete signup from email.');
      navigate(`/workspace/employees/${body.id}`, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to convert employee.');
    } finally {
      setConvertSaving(false);
    }
  };

  const downgradeToSimple = async () => {
    if (!details?.user || details.isPrimaryAdmin) return;
    if (!window.confirm('Remove system login for this person? Their user account will be blocked and they become a simple employee record.')) {
      return;
    }
    setDowngradeSaving(true);
    setError(null);
    setActionMessage(null);
    try {
      const res = await authorizedFetch(`${API_BASE}/auth/workspace/employees/${employeeId}/convert-to-simple`, {
        method: 'POST',
      });
      if (!res.ok) throw new Error(await resolveApiErrorMessage(res, 'Unable to downgrade employee.'));
      const body = (await res.json()) as { id: string };
      setActionMessage('Login access removed. Profile is now a simple employee record.');
      navigate(`/workspace/employees/${body.id}`, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to downgrade employee.');
    } finally {
      setDowngradeSaving(false);
    }
  };

  const toggleGlobalRole = (roleId: string, checked: boolean) => {
    setGlobalRoleIds((prev) => {
      if (checked) return Array.from(new Set([...prev, roleId]));
      return prev.filter((id) => id !== roleId);
    });
  };

  const userTypeLabel = (type: string) => {
    const u = String(type || '').toUpperCase();
    if (u === 'ADMIN') return 'Administrator';
    if (u === 'SHIPPING_AGENT') return 'Shipping agent';
    if (u === 'EMPLOYEE') return 'Employee';
    return type || '—';
  };

  return (
    <MainLayout withSidebar sidebarItems={getBackofficeSidebarItems(t)} activePath={location.pathname}>
      <main className="workspace-page">
        <section className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="workspace-page-title">
              Employee details
            </h1>
            <p className="workspace-page-subtitle text-slate-600">
              Full profile, access, roles, and compensation. The team list is overview-only.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigate('/workspace/employees')}
              className="rounded-full border border-slate-300 bg-white px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-700 hover:border-slate-400"
            >
              Back to list
            </button>
            <button
              type="button"
              onClick={() => navigate('/workspace/payroll')}
              className="rounded-full border border-indigo-300 bg-indigo-50 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-indigo-700 hover:border-indigo-400 hover:bg-indigo-100"
            >
              Payroll
            </button>
          </div>
        </section>

        {error ? (
          <section className="glass-panel p-4">
            <p className="text-xs text-red-500">{error}</p>
          </section>
        ) : null}

        {loading || !details ? (
          <section className="workspace-panel">
            <p className="text-sm text-slate-600">{t('common.loading', 'Loading…')}</p>
          </section>
        ) : (
          <>
            <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <div className="glass-panel p-4 text-sm">
                <p className="text-[11px] uppercase tracking-wide text-slate-500">Employee code</p>
                <p className="mt-1 font-semibold text-slate-900">{details.employeeCode}</p>
              </div>
              <div className="glass-panel p-4 text-sm">
                <p className="text-[11px] uppercase tracking-wide text-slate-500">Full name</p>
                <p className="mt-1 font-semibold text-slate-900">{details.fullName}</p>
              </div>
              <div className="glass-panel p-4 text-sm">
                <p className="text-[11px] uppercase tracking-wide text-slate-500">Access type</p>
                <p className="mt-1 font-semibold text-slate-900">
                  {details.employmentKind === 'SYSTEM_USER' ? 'System user' : 'Simple employee (no login)'}
                </p>
              </div>
              <div className="glass-panel p-4 text-sm">
                <p className="text-[11px] uppercase tracking-wide text-slate-500">Status</p>
                <p className="mt-1 font-semibold text-slate-900">{details.status}</p>
                {details.user && details.personnelRecordStatus && details.personnelRecordStatus !== details.user.status ? (
                  <p className="mt-1 text-[10px] text-slate-500">
                    Personnel record: {details.personnelRecordStatus}
                  </p>
                ) : null}
              </div>
            </section>

            <section className="workspace-panel">
              <h2 className="mb-3 text-sm font-semibold text-slate-900">Contact &amp; account</h2>
              <dl className="grid gap-3 text-sm sm:grid-cols-2">
                <div>
                  <dt className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Work email</dt>
                  <dd className="mt-0.5 text-slate-900">{details.workEmail || details.user?.email || '—'}</dd>
                </div>
                <div>
                  <dt className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Phone</dt>
                  <dd className="mt-0.5 text-slate-900">{details.phone || '—'}</dd>
                </div>
                {details.user ? (
                  <>
                    <div>
                      <dt className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Login email</dt>
                      <dd className="mt-0.5 text-slate-900">{details.user.email}</dd>
                    </div>
                    <div>
                      <dt className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Account type</dt>
                      <dd className="mt-0.5 text-slate-900">{userTypeLabel(details.user.type)}</dd>
                    </div>
                    <div>
                      <dt className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Email verified</dt>
                      <dd className="mt-0.5 text-slate-900">{details.user.isEmailVerified ? 'Yes' : 'No'}</dd>
                    </div>
                  </>
                ) : null}
                <div>
                  <dt className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Created</dt>
                  <dd className="mt-0.5 text-slate-900">{formatDate(details.createdAt)}</dd>
                </div>
                {details.blockedReason ? (
                  <div className="sm:col-span-2">
                    <dt className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Block reason</dt>
                    <dd className="mt-0.5 text-slate-900">
                      {details.blockedReason}
                      {details.blockedReason === 'INTRUSION_SELF_REPORT' ? (
                        <span className="ml-2 text-[11px] font-semibold text-amber-800">
                          (
                          {t(
                            'backoffice.employees_blocked_intrusion',
                            'Self-service intrusion report',
                          )}
                          )
                        </span>
                      ) : null}
                    </dd>
                  </div>
                ) : null}
                {details.blockedAt ? (
                  <div>
                    <dt className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Blocked at</dt>
                    <dd className="mt-0.5 text-slate-900">{formatDate(details.blockedAt)}</dd>
                  </div>
                ) : null}
              </dl>
              {details.notes ? (
                <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50/80 p-3 text-xs text-slate-700">
                  <span className="font-semibold text-slate-800">Notes: </span>
                  {details.notes}
                </div>
              ) : null}
            </section>

            <section className="workspace-panel">
              <h2 className="mb-2 text-sm font-semibold text-slate-900">Access &amp; security</h2>
              <p className="mb-4 text-xs text-slate-500">
                Block or re-enable this record. For accounts with login, this updates the user and keeps the personnel
                record in sync.
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => void toggleStatus()}
                  disabled={statusSaving || details.isPrimaryAdmin || isSelf}
                  className={`rounded-full border px-4 py-2 text-[11px] font-semibold uppercase tracking-wide disabled:cursor-not-allowed disabled:opacity-50 ${
                    details.status === 'ACTIVE'
                      ? 'border-rose-300 bg-rose-50 text-rose-700 hover:border-rose-400'
                      : 'border-emerald-300 bg-emerald-50 text-emerald-700 hover:border-emerald-400'
                  }`}
                >
                  {statusSaving
                    ? 'Updating…'
                    : details.status === 'ACTIVE'
                      ? t('backoffice.employees_action_disable', 'Block')
                      : t('backoffice.employees_action_enable', 'Unblock')}
                </button>
                {details.isPrimaryAdmin ? (
                  <span className="text-xs text-amber-800">Primary administrator cannot be blocked here.</span>
                ) : null}
                {isSelf ? <span className="text-xs text-slate-600">You cannot change your own status.</span> : null}
              </div>

              {details.employmentKind === 'SIMPLE' ? (
                <div className="mt-6 border-t border-slate-100 pt-6">
                  <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-800">
                    Grant system login
                  </h3>
                  <p className="mb-3 text-xs text-slate-500">
                    Creates a user account, sends an invite email, and links this personnel record. Department access
                    matches the departments already assigned below.
                  </p>
                  <div className="flex max-w-xl flex-col gap-3 sm:flex-row sm:items-end">
                    <div className="flex-1">
                      <label className="mb-1 block text-[11px] font-medium text-slate-600" htmlFor="convertEmail">
                        Work email
                      </label>
                      <input
                        id="convertEmail"
                        type="email"
                        value={convertEmail}
                        onChange={(e) => setConvertEmail(e.target.value)}
                        className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs text-slate-900"
                        placeholder="name@company.com"
                      />
                    </div>
                    <div className="w-full sm:w-48">
                      <label className="mb-1 block text-[11px] font-medium text-slate-600" htmlFor="convertRole">
                        Default role
                      </label>
                      <select
                        id="convertRole"
                        value={convertRoleId}
                        onChange={(e) => setConvertRoleId(e.target.value)}
                        className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs text-slate-900"
                      >
                        <option value="">Select…</option>
                        {rolesCatalog.map((r) => (
                          <option key={r.id} value={r.id}>
                            {r.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <button
                      type="button"
                      onClick={() => void convertToUser()}
                      disabled={convertSaving || !convertEmail.trim() || !convertRoleId}
                      className="rounded-full bg-brand-600 px-4 py-2 text-[11px] font-semibold uppercase tracking-wide text-white hover:bg-brand-500 disabled:opacity-50"
                    >
                      {convertSaving ? 'Working…' : 'Convert & invite'}
                    </button>
                  </div>
                </div>
              ) : null}

              {details.employmentKind === 'SYSTEM_USER' && details.user && !details.isPrimaryAdmin ? (
                <div className="mt-6 border-t border-slate-100 pt-6">
                  <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-800">
                    Remove login access
                  </h3>
                  <p className="mb-3 text-xs text-slate-500">
                    Blocks the user account and keeps a simple personnel record with the same departments.
                  </p>
                  <button
                    type="button"
                    onClick={() => void downgradeToSimple()}
                    disabled={downgradeSaving || isSelf}
                    className="rounded-full border border-amber-300 bg-amber-50 px-4 py-2 text-[11px] font-semibold uppercase tracking-wide text-amber-800 hover:border-amber-400 disabled:opacity-50"
                  >
                    {downgradeSaving ? 'Working…' : 'Downgrade to simple employee'}
                  </button>
                  {isSelf ? (
                    <p className="mt-2 text-xs text-slate-600">You cannot downgrade your own account.</p>
                  ) : null}
                </div>
              ) : null}
            </section>

            {canManageRoles ? (
              <section className="workspace-panel">
                <h2 className="mb-2 text-sm font-semibold text-slate-900">Workspace roles</h2>
                <p className="mb-4 text-xs text-slate-500">
                  Global roles apply across the workspace (separate from per-department roles below).
                </p>
                {rolesSectionLoading ? (
                  <p className="text-sm text-slate-600">{t('common.loading', 'Loading…')}</p>
                ) : (
                  <>
                    <div className="max-h-48 space-y-2 overflow-auto rounded-xl border border-slate-100 p-3">
                      {rolesCatalog.length === 0 ? (
                        <p className="text-xs text-slate-600">No assignable roles.</p>
                      ) : (
                        rolesCatalog.map((r) => (
                          <label key={r.id} className="flex cursor-pointer items-center gap-2 text-xs text-slate-800">
                            <input
                              type="checkbox"
                              checked={globalRoleIds.includes(r.id)}
                              onChange={(e) => toggleGlobalRole(r.id, e.target.checked)}
                              className="h-4 w-4 rounded border-slate-300 text-brand-600"
                            />
                            {r.name}
                          </label>
                        ))
                      )}
                    </div>
                    <div className="mt-3 flex justify-end">
                      <button
                        type="button"
                        onClick={() => void saveGlobalRoles()}
                        disabled={globalRolesSaving}
                        className="rounded-full bg-slate-900 px-4 py-2 text-xs font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
                      >
                        {globalRolesSaving ? t('common.saving', 'Saving…') : 'Save workspace roles'}
                      </button>
                    </div>
                  </>
                )}
              </section>
            ) : null}

            {canManageRoles ? (
              <section className="workspace-panel">
                <h2 className="mb-2 text-sm font-semibold text-slate-900">Department roles</h2>
                <p className="mb-4 text-xs text-slate-500">
                  One primary role per department (used for scoped permissions).
                </p>
                {rolesSectionLoading ? (
                  <p className="text-sm text-slate-600">{t('common.loading', 'Loading…')}</p>
                ) : (
                  <>
                    <div className="max-h-[50vh] space-y-2 overflow-auto rounded-xl border border-slate-100 p-3">
                      {allDepartments.map((dep) => (
                        <div key={dep.id} className="rounded-lg border border-slate-100 bg-white px-3 py-2 text-xs">
                          <div className="mb-1 font-semibold text-slate-900">{dep.name}</div>
                          <select
                            className="w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-900"
                            value={deptRoleByDepartmentId[dep.id] || ''}
                            onChange={(e) =>
                              setDeptRoleByDepartmentId((prev) => ({
                                ...prev,
                                [dep.id]: e.target.value,
                              }))}
                          >
                            <option value="">No role</option>
                            {rolesCatalog.map((r) => (
                              <option key={r.id} value={r.id}>
                                {r.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      ))}
                    </div>
                    <div className="mt-3 flex justify-end">
                      <button
                        type="button"
                        onClick={() => void saveDepartmentRoles()}
                        disabled={deptRolesSaving}
                        className="rounded-full bg-slate-900 px-4 py-2 text-xs font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
                      >
                        {deptRolesSaving ? t('common.saving', 'Saving…') : 'Save department roles'}
                      </button>
                    </div>
                  </>
                )}
              </section>
            ) : (
              <section className="workspace-panel">
                <h2 className="mb-2 text-sm font-semibold text-slate-900">Roles</h2>
                <p className="text-xs text-slate-600">
                  This person has no system login. Grant access above to manage workspace and department roles.
                </p>
              </section>
            )}

            <section className="workspace-panel">
              <h2 className="mb-2 text-sm font-semibold text-slate-900">Assigned departments</h2>
              <div className="overflow-x-auto">
                <table className="min-w-full text-left text-xs sm:text-sm">
                  <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="py-2 pr-4">Department</th>
                      <th className="py-2 pr-4">Slug</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {details.departments.length === 0 ? (
                      <tr>
                        <td colSpan={2} className="py-3 text-slate-600">
                          No departments on this record yet. Add departments when creating the employee or adjust in the
                          database workflow your team uses.
                        </td>
                      </tr>
                    ) : (
                      details.departments.map((dep) => (
                        <tr key={dep.id}>
                          <td className="py-2 pr-4 font-medium text-slate-900">{dep.name}</td>
                          <td className="py-2 pr-4 text-slate-600">/{dep.slug}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="workspace-panel">
              <h2 className="mb-2 text-sm font-semibold text-slate-900">Compensation by department</h2>
              <p className="mb-4 text-xs text-slate-500">
                Salary is configured per department. Payroll and budget deductions use this split automatically.
              </p>
              <div className="mb-3 grid gap-2 md:grid-cols-3">
                <input
                  type="text"
                  value={salaryCurrency}
                  onChange={(e) => setSalaryCurrency(e.target.value.toUpperCase())}
                  className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs text-slate-900"
                  placeholder="Currency"
                />
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-left text-xs sm:text-sm">
                  <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="py-2 pr-4">Department</th>
                      <th className="py-2 pr-4">Slug</th>
                      <th className="py-2 pr-4">Monthly base salary</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {details.departments.map((dep) => (
                      <tr key={dep.id}>
                        <td className="py-2 pr-4 font-medium text-slate-900">{dep.name}</td>
                        <td className="py-2 pr-4 text-slate-600">/{dep.slug}</td>
                        <td className="py-2 pr-4">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={departmentSalaries[dep.id] || ''}
                            onChange={(e) =>
                              setDepartmentSalaries((prev) => ({
                                ...prev,
                                [dep.id]: e.target.value,
                              }))}
                            className="w-44 rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-900"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm font-semibold text-slate-900">
                  Total base salary: {computedTotal.toFixed(2)} {salaryCurrency}
                </p>
                <button
                  type="button"
                  onClick={() => void saveCompensation()}
                  disabled={saving}
                  className="rounded-full bg-slate-900 px-4 py-2 text-xs font-semibold text-white shadow-sm hover:bg-slate-800 disabled:opacity-60"
                >
                  {saving ? t('common.saving', 'Saving…') : t('common.save', 'Save compensation')}
                </button>
              </div>
            </section>
          </>
        )}
      </main>

      {actionMessage ? (
        <div
          className="pointer-events-none fixed inset-x-0 bottom-0 z-[200] flex justify-center p-4 sm:p-6"
          role="status"
          aria-live="polite"
          aria-atomic="true"
        >
          <div
            className="pointer-events-auto flex w-full max-w-lg items-start gap-3 rounded-2xl border border-emerald-200/90 bg-white px-4 py-4 shadow-xl shadow-slate-900/15 ring-1 ring-emerald-500/10 sm:px-5 sm:py-4"
          >
            <div
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700"
              aria-hidden
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.25}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div className="min-w-0 flex-1 pt-0.5">
              <p className="text-sm font-semibold text-slate-900">
                {t('common.success', 'Success')}
              </p>
              <p className="mt-1 text-sm leading-relaxed text-slate-600">
                {actionMessage}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setActionMessage(null)}
              className="shrink-0 rounded-lg p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
              aria-label={t('common.dismiss', 'Dismiss')}
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      ) : null}
    </MainLayout>
  );
}
